// Global imports
import { useMemo } from 'react'
import { Modal, Heading, HelpPopover } from '~/components'
import { PickupBranchCard } from '~/components/partials/basket'
import { useProductSingleContext } from '~/context/productSingle'

/**
 * PDP "Change branch" modal for Click & Collect.
 *
 * Doesn't fetch on open — every branch is already on
 * `charges.product.pickupPoints` from the prices fetch. Selecting a
 * branch calls `selectBranch`, which re-fetches for that branch (so
 * pricing is correct) while preserving the branch list.
 */
const ProductPickupModal = () => {
  const { charges, pickupModalOpen, setPickupModalOpen, selectBranch } =
    useProductSingleContext()

  // All branches the prices fetch returned for this address.
  const points = charges?.product.pickupPoints ?? []

  // Add-ons from the charges response — used for mandatory add-on
  // branch-stock checks in each row.
  const addOns = charges?.product.addOns ?? []

  // The branch currently committed (chosen override, else nearest).
  const currentBranchId = charges?.pickup.branchId

  // Quantity the current charges were fetched against — a PDP quantity
  // change always re-fetches, so this tracks the live selection.
  const itemQuantity = Number(charges?.request.quantity) || 1

  // The date current-stock branches are anchored on. Use the date the
  // final charges fetch was actually made for (`submittedStartDate`):
  // the branch stock is valid for that date, and it reflects any
  // correction shift (e.g. a quantity bump moving the start to a later
  // date) while not being pushed forward by the auto-selected branch's
  // own future date the way `resolvedStartDate` can be. Future-stock
  // branches still surface their own `firstAvailableDate`.
  const requestedStartDate = charges?.request.submittedStartDate ?? ''

  // Mandatory linked add-ons gate branch viability (their stock at a
  // branch must cover the parent). On the PDP nothing is in the basket
  // yet, so the mandatory extras stand in for the linked children.
  const basketLinkedStockCodes = useMemo(
    () =>
      (charges?.product.addOns ?? [])
        .filter(addOn => addOn.isMandatory)
        .map(addOn => addOn.stockCode),
    [charges]
  )

  return (
    <Modal
      open={pickupModalOpen}
      setOpen={() => setPickupModalOpen(false)}
      size="md"
    >
      <div className="mb-6 flex items-center gap-2">
        <Heading
          tag="h2"
          size="h5"
          className="mb-0"
        >
          Choose a different branch
        </Heading>
        <HelpPopover
          title="Please note:"
          description={`Changing pickup locations may change hire dates of products
          based on availability.`}
          size="md"
        />
      </div>
      {points.length === 0 ? (
        <p>No pickup options available for this address</p>
      ) : (
        <>
          <h3 className="font-semibold">Nearest stores to your location:</h3>
          {points.map(point => (
            <PickupBranchCard
              key={point.id}
              point={point}
              itemQuantity={itemQuantity}
              addOns={addOns}
              basketLinkedStockCodes={basketLinkedStockCodes}
              isActive={point.id === currentBranchId}
              requestedStartDate={requestedStartDate}
              onSelect={selectBranch}
            />
          ))}
        </>
      )}
    </Modal>
  )
}

export default ProductPickupModal
